// The ballot rules that are worth getting right once, in one place.
//
// Pure functions, no database: the routes feed them rows and act on what
// comes back, and the tests exercise them from the sim runner the way
// standing.test.js does. Anything with a tie-break or a deadline in it
// lives here rather than inline in a route, because inline is where two
// routes quietly disagree.

// The viewer's copy of what can be scheduled where. The sim validates
// again at addEntry (its CALENDAR_KINDS is the authority); this exists so
// the admin form can refuse nonsense without a round trip.
export const BALLOT_KINDS = {
  exhibition: ['museum'],
  film: ['cinema'],
  gig: ['rave', 'bar'],
  special: ['chinese', 'junk', 'bakery', 'caffeine', 'bar'],
}

/** Voting needs him to know you: one MATURED kept offering (the same
 *  day-old basis as the submit allowance, so a fresh sybil farm waits a
 *  day before its votes count for anything). */
export const VOTE_MIN_ACCEPTED = 1

/**
 * Who won. Highest vote count. Zero votes in total is nobody's win.
 *
 * Ties used to go to the option listed first, on the reasoning that an
 * admin ordered them and "first listed wins" is a rule a person can hold
 * in their head. That was written before people could propose their own
 * events, and a proposal always sits at position 0 with the decline under
 * it, so the rule quietly meant "the proposer wins every tie".
 *
 * It shipped one: "Bonnie's Blues" took 1 vote, "leave it" took 1 vote,
 * and the gig went up on the wall with a poster because it was printed
 * first. The town had said no exactly as often as it said yes.
 *
 * So a decline wins any tie it is part of. Something the town is evenly
 * split on is not something the town asked for, and the cost of getting
 * this wrong is asymmetric: a rejected gig can be proposed again next
 * week, an unwanted one is already on the wall with a poster up.
 *
 * @param {Array<{id:string, position:number, is_decline?:boolean, isDecline?:boolean}>} options
 * @param {Record<string, number>} tally  optionId -> votes
 * @returns {string|null} winning option id
 */
export function winnerOf(options, tally) {
  const sorted = [...options].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
  let bestVotes = 0
  for (const o of sorted) bestVotes = Math.max(bestVotes, tally[o.id] || 0)
  if (bestVotes === 0) return null

  const tied = sorted.filter((o) => (tally[o.id] || 0) === bestVotes)
  // Column naming crosses a boundary here: the db row says is_decline and
  // the ballot spec says isDecline. Read both or the guard is decorative.
  const decline = tied.find((o) => o.is_decline || o.isDecline)
  return (decline || tied[0]).id
}

/**
 * Sanity for a new ballot. Times are ms; the event must start after the
 * vote closes (a poster for a thing the town has not chosen yet would be
 * the world lying), and everything must be ahead of now.
 */
export function validateBallot({ question, kind, place, options, closesAt, startsAt, endsAt }, now = Date.now()) {
  const q = String(question || '').trim()
  if (q.length < 8 || q.length > 120) return 'question must be 8 to 120 characters'
  const places = BALLOT_KINDS[kind]
  if (!places) return `unknown kind: ${kind}`
  if (!places.includes(place)) return `a ${kind} cannot happen at "${place}" (allowed: ${places.join(', ')})`
  if (!Array.isArray(options) || options.length < 2 || options.length > 6) return 'between 2 and 6 options'
  for (const o of options) {
    const t = String(o?.title || '').trim()
    if (t.length < 2 || t.length > 80) return 'every option needs a title, 2 to 80 characters'
    if (String(o?.blurb || '').length > 300) return 'option blurbs stop at 300 characters'
  }
  const c = Number(closesAt), s = Number(startsAt), e = Number(endsAt)
  if (![c, s, e].every(Number.isFinite)) return 'closesAt, startsAt and endsAt must be timestamps (ms)'
  if (c <= now) return 'the vote has to close in the future'
  if (s < c) return 'the event cannot start before the vote closes'
  if (e <= s) return 'the event has to end after it starts'
  if (e - s > 90 * 24 * 3600 * 1000) return 'nothing runs for more than 90 days'
  return null
}
